'use client';

import { useState, useMemo, useEffect } from 'react';
import { TechnologyOption } from '@/lib/tech-stack-types';
import TechStackRadar from '@/components/visualizations/TechStackRadar';
import TechStackMarketShare from '@/components/visualizations/TechStackMarketShare';
import TechStackScatterPlot from '@/components/visualizations/TechStackScatterPlot';
import TechStackTrendCharts from '@/components/visualizations/TechStackTrendCharts';
import TechStackHeatmap from '@/components/visualizations/TechStackHeatmap';
import TechStackVisualizationFilters, {
  applyFilters,
  VisualizationFilters,
} from '@/components/visualizations/TechStackVisualizationFilters';
import Link from 'next/link';

const initialFilters: VisualizationFilters = {
  categories: [],
  maturityLevels: [],
  licenseTypes: [],
  complianceRequirements: [],
  popularityTrends: [],
  communitySizes: [],
  priceRanges: [],
};

export default function TechStackVisualizationsPage() {
  const [technologies, setTechnologies] = useState<TechnologyOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<VisualizationFilters>(initialFilters);
  const [activeView, setActiveView] = useState<'radar' | 'market' | 'scatter' | 'trends' | 'heatmap'>('radar');

  // Fetch technologies from API
  useEffect(() => {
    const fetchTechnologies = async () => {
      try {
        const response = await fetch('/api/public/technologies?limit=500');
        if (!response.ok) {
          throw new Error('Failed to fetch technologies');
        }
        const data = await response.json();
        setTechnologies(data.technologies);
        setError(null);
      } catch (err) {
        console.error('Error fetching technologies:', err);
        setError('Failed to load technologies');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTechnologies();
  }, []);

  // Apply filters to technology database
  const filteredTechnologies = useMemo(
    () => applyFilters(technologies, filters),
    [technologies, filters]
  );

  const getActiveFilterCount = () => {
    return Object.values(filters).reduce((sum, arr) => sum + arr.length, 0);
  };

  const views = [
    { id: 'radar', label: 'Tech Radar', icon: '🎯' },
    { id: 'market', label: 'Market Share', icon: '📊' },
    { id: 'scatter', label: 'Maturity vs Community', icon: '📈' },
    { id: 'trends', label: 'Popularity Trends', icon: '📉' },
    { id: 'heatmap', label: 'Comparison Matrix', icon: '🔥' },
  ] as const;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-7xl mx-auto text-center py-24">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-accent border-t-transparent mb-6"></div>
          <p className="text-foreground text-lg">Loading technology database...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-7xl mx-auto text-center py-24">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-8 max-w-md mx-auto">
            <div className="text-4xl mb-4">⚠️</div>
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const categoryCount = new Set(technologies.map(t => t.category)).size;

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-foreground-tertiary mb-4">
            <Link href="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link href="/tools/tech-stack-decision" className="hover:text-foreground transition-colors">
              Tech Stack Decision Engine
            </Link>
            <span>/</span>
            <span className="text-foreground">Visualizations</span>
          </div>

          {/* Title */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">
                Technology Landscape Visualizations
              </h1>
              <p className="text-foreground-secondary text-lg">
                Explore and compare {technologies.length} technologies across multiple dimensions
              </p>
            </div>

            <Link
              href="/tools/tech-stack-decision"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
            >
              ← Back to Decision Engine
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-surface rounded-lg p-4">
              <div className="text-sm text-foreground-secondary mb-1">Total Technologies</div>
              <div className="text-2xl font-bold text-accent">{technologies.length}</div>
            </div>
            <div className="bg-surface rounded-lg p-4">
              <div className="text-sm text-foreground-secondary mb-1">Filtered Results</div>
              <div className="text-2xl font-bold text-accent">{filteredTechnologies.length}</div>
            </div>
            <div className="bg-surface rounded-lg p-4">
              <div className="text-sm text-foreground-secondary mb-1">Active Filters</div>
              <div className="text-2xl font-bold text-accent">{getActiveFilterCount()}</div>
            </div>
            <div className="bg-surface rounded-lg p-4">
              <div className="text-sm text-foreground-secondary mb-1">Categories</div>
              <div className="text-2xl font-bold text-accent">{categoryCount}</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <TechStackVisualizationFilters
            technologies={technologies}
            filters={filters}
            onFiltersChange={setFilters}
          />
        </div>

        {/* View Selector */}
        <div className="mb-8 bg-surface rounded-lg p-2">
          <div className="flex flex-wrap gap-2">
            {views.map(view => (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                  activeView === view.id
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-background text-foreground-secondary hover:bg-background/80'
                }`}
              >
                <span className="text-xl">{view.icon}</span>
                <span className="hidden sm:inline">{view.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* No Results Message */}
        {filteredTechnologies.length === 0 && (
          <div className="bg-surface rounded-lg p-12 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-foreground mb-2">No Technologies Found</h3>
            <p className="text-foreground-secondary mb-6">
              Try adjusting your filters to see more results
            </p>
            <button
              onClick={() => setFilters(initialFilters)}
              className="px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* Visualizations */}
        {filteredTechnologies.length > 0 && (
          <div className="space-y-12">
            {activeView === 'radar' && (
              <div className="bg-surface/50 rounded-lg p-6">
                <TechStackRadar technologies={filteredTechnologies} />
              </div>
            )}

            {activeView === 'market' && (
              <div className="bg-surface/50 rounded-lg p-6">
                <TechStackMarketShare technologies={filteredTechnologies} />
              </div>
            )}

            {activeView === 'scatter' && (
              <div className="bg-surface/50 rounded-lg p-6">
                <TechStackScatterPlot technologies={filteredTechnologies} />
              </div>
            )}

            {activeView === 'trends' && (
              <div className="bg-surface/50 rounded-lg p-6">
                <TechStackTrendCharts technologies={filteredTechnologies} />
              </div>
            )}

            {activeView === 'heatmap' && (
              <div className="bg-surface/50 rounded-lg p-6">
                <TechStackHeatmap technologies={filteredTechnologies} />
              </div>
            )}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 bg-surface rounded-lg p-6">
          <h3 className="text-xl font-bold text-foreground mb-4">How to Use These Visualizations</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <div className="text-2xl mb-2">🎯</div>
              <h4 className="font-semibold text-foreground mb-2">Technology Radar</h4>
              <p className="text-sm text-foreground-secondary">
                Explore technologies positioned by adoption status (Adopt, Trial, Assess, Hold) across four quadrants.
                Click technologies for details.
              </p>
            </div>

            <div>
              <div className="text-2xl mb-2">📊</div>
              <h4 className="font-semibold text-foreground mb-2">Market Share</h4>
              <p className="text-sm text-foreground-secondary">
                View technology distribution by category, license type, or maturity level.
                Based on community size and popularity.
              </p>
            </div>

            <div>
              <div className="text-2xl mb-2">📈</div>
              <h4 className="font-semibold text-foreground mb-2">Maturity vs Community</h4>
              <p className="text-sm text-foreground-secondary">
                Compare technologies on two axes. Top-right quadrant shows safe bets (mature + large community).
                Bubble size = popularity trend.
              </p>
            </div>

            <div>
              <div className="text-2xl mb-2">📉</div>
              <h4 className="font-semibold text-foreground mb-2">Popularity Trends</h4>
              <p className="text-sm text-foreground-secondary">
                Track technology popularity over time. Compare up to 5 technologies across GitHub stars, NPM downloads, or Stack Overflow activity.
              </p>
            </div>

            <div>
              <div className="text-2xl mb-2">🔥</div>
              <h4 className="font-semibold text-foreground mb-2">Comparison Matrix</h4>
              <p className="text-sm text-foreground-secondary">
                Heatmap showing all technologies scored across compliance, technical, operational, cost, ecosystem, and maturity dimensions.
              </p>
            </div>

            <div>
              <div className="text-2xl mb-2">🎛️</div>
              <h4 className="font-semibold text-foreground mb-2">Filters</h4>
              <p className="text-sm text-foreground-secondary">
                Use filters to focus on specific categories, maturity levels, license types, compliance requirements, and more.
              </p>
            </div>
          </div>
        </div>

        {/* Integration Links */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <Link
            href="/tools/tech-stack-decision"
            className="bg-surface rounded-lg p-6 hover:bg-surface/80 transition-colors border border-border-muted hover:border-border-muted0"
          >
            <div className="text-2xl mb-2">🤖</div>
            <h4 className="font-semibold text-foreground mb-2">Get Personalized Recommendations</h4>
            <p className="text-sm text-foreground-secondary mb-3">
              Answer questions about your requirements and get AI-powered technology recommendations with detailed scoring.
            </p>
            <span className="text-accent text-sm font-semibold">Start Questionnaire →</span>
          </Link>

          <Link
            href="/tools/tech-stack-decision/compare"
            className="bg-surface rounded-lg p-6 hover:bg-surface/80 transition-colors border border-border-muted hover:border-border-muted0"
          >
            <div className="text-2xl mb-2">⚖️</div>
            <h4 className="font-semibold text-foreground mb-2">Head-to-Head Comparison</h4>
            <p className="text-sm text-foreground-secondary mb-3">
              Compare 2-5 technologies side-by-side with detailed feature comparisons, pros/cons, and use case analysis.
            </p>
            <span className="text-accent text-sm font-semibold">Compare Technologies →</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
